import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { Button, ButtonType } from '@/components/atoms/button'
import { InputField } from '@/components/molecules/input-field'
import { useErrorHandler } from '@/hooks/use-error-handler'
import {
  ChildrenClass,
  SalesSubjectTaxPriceType,
  SalesSubjectTaxRoundingType,
  SalesSubjectTaxRoundingUnit,
  SalesSubjectTaxType,
} from '@/models/sales-manager/sales'
import { LoaderContextCreator } from '@/contexts/loader'
import * as api from '@/apis/aipass'
import { InputSalesSubject } from '@/components/molecules/settings/asset/space/input-sales-subject'
import { Checkbox } from '@/components/atoms/checkbox'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { SubjectTaxPriceRangeField } from './subject-tax-price-range-field'
import { SubjectTaxRateField } from './subject-tax-rate-field'
import { DateField } from '@/components/molecules/date-field'
import moment from 'moment'

type Props = {
  detail: SalesSubjectTaxType | null
  onCancel: () => void
  onSave: () => void
}

export type FormValue = {
  name: string
  childrenClass: ChildrenClass[]
  isIncludeDayUse: boolean
  isIncludeInReservationPrice: boolean
  salesDepartmentId: string
  salesSubjectMasterId: string
  priceType: SalesSubjectTaxPriceType
  validFrom: string | undefined
  validTo: string | undefined
  roomChargeRate: number | undefined
  roomChargeRange: Array<{
    upperPrice: number | undefined
    lowerPrice: number | undefined
    taxPrice: number | undefined
  }>
  roomChargeRoundingUnit: SalesSubjectTaxRoundingUnit | undefined
  roomChargeRoundingType: SalesSubjectTaxRoundingType | undefined
  roomChargeLimitEnabled: boolean
  roomChargeUpperLimit: number | null
  roomChargeLowerLimit: number | null
}

// roomChargeLimitEnabled を除いた API 送信用の型
export type SalesSubjectTaxPayload = Omit<FormValue, 'roomChargeLimitEnabled'>

export const SubjectTaxDetailModal: React.FC<Props> = ({ detail, onCancel, onSave: parentSave }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid, errors },
    resetField,
  } = useForm<FormValue>({
    mode: 'all',
    defaultValues: {
      name: detail?.name || '',
      childrenClass: detail?.childrenClass || Object.values(ChildrenClass),
      isIncludeDayUse: detail?.isIncludeDayUse || false,
      isIncludeInReservationPrice: detail?.isIncludeInReservationPrice || false,
      salesDepartmentId: detail?.salesSubject.salesDepartmentId || undefined,
      salesSubjectMasterId: detail?.salesSubject.salesSubjectMasterId || undefined,
      priceType: detail?.priceType || SalesSubjectTaxPriceType.FixAmount,
      validFrom: detail?.validFrom ?? undefined,
      validTo: detail?.validTo ?? undefined,
      roomChargeRate: detail?.priceType === SalesSubjectTaxPriceType.FixRate ? Number(detail.roomChargeRate) : undefined,
      roomChargeRange:
        detail?.priceType === SalesSubjectTaxPriceType.FixAmount
          ? detail.roomChargeRange
          : [
              {
                upperPrice: undefined,
                lowerPrice: undefined,
                taxPrice: undefined,
              },
            ],
      roomChargeRoundingUnit:
        detail?.priceType === SalesSubjectTaxPriceType.FixRate ? detail.roomChargeRoundingUnit : SalesSubjectTaxRoundingUnit.Hundred,
      roomChargeRoundingType:
        detail?.priceType === SalesSubjectTaxPriceType.FixRate ? detail.roomChargeRoundingType : SalesSubjectTaxRoundingType.RoundDown,
      roomChargeLimitEnabled:
        detail?.priceType === SalesSubjectTaxPriceType.FixRate
          ? detail.roomChargeUpperLimit != null || detail.roomChargeLowerLimit != null
          : false,
      roomChargeUpperLimit: detail?.priceType === SalesSubjectTaxPriceType.FixRate ? detail.roomChargeUpperLimit : null,
      roomChargeLowerLimit: detail?.priceType === SalesSubjectTaxPriceType.FixRate ? detail.roomChargeLowerLimit : null,
    },
  })
  const watchPriceType = useWatch({ control, name: 'priceType' })
  const watchValidFrom = useWatch({ control, name: 'validFrom' })
  const watchValidTo = useWatch({ control, name: 'validTo' })

  const onSave = async (value: FormValue) => {
    try {
      setIsLoading(true)
      // roomChargeLimitEnabled はUI専用フィールドのため除外して送信する
      const { roomChargeLimitEnabled, ...payload } = value
      if (detail) {
        await api.updateSalesSubSubjectTax(detail.id, payload)
      } else {
        await api.createSalesSubSubjectTax(payload)
      }
      parentSave()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal customCss={modalStyle} containerCss={css({ pointerEvents: 'none' })}>
        <ModalHeader>{t(detail ? 'saleSubjectTax.EditTax' : 'saleSubjectTax.AddTax')}</ModalHeader>
        <ModalBody>
          <div css={modalBodyStyle}>
            <div>
              <div css={inputTextStyle}>
                {t('saleSubjectTax.Name')}
                <div css={requireLabelTextStyle}>※</div>
              </div>
              <Controller
                control={control}
                rules={{ required: t('Required field has not been entered') }}
                name="name"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <InputField
                    value={value}
                    handleChangeData={onChange}
                    error={error?.message}
                    placeholder={t('saleSubjectTax.Tax')}
                    marginBottom={24}
                  />
                )}
              />
            </div>

            <div style={{ marginBottom: 24, position: 'relative' }}>
              <div css={inputTextStyle}>{t('saleSubjectTax.TargetChildClassification')}</div>
              <Controller
                control={control}
                name="childrenClass"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 32px' }}>
                      {Object.values(ChildrenClass).map(classVal => (
                        <Checkbox
                          key={classVal}
                          value={value.includes(classVal)}
                          onChange={v => onChange(v ? [...value, classVal] : value.filter(val => val !== classVal))}
                          label={t(`ChildrenClass.${classVal}`)}
                        />
                      ))}
                    </div>
                  </>
                )}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <div css={inputTextStyle}>
                {t('saleSubjectTax.IncludesDayUse')}
                <div css={requireLabelTextStyle}>※</div>
              </div>
              <Controller
                control={control}
                name="isIncludeDayUse"
                render={({ field: { onChange, value } }) => (
                  <RadioGroupField
                    onChange={e => onChange(e.currentTarget.value === '1')}
                    value={value ? '1' : '0'}
                    items={[
                      { value: '1', label: t('saleSubjectTax.Include') },
                      { value: '0', label: t('saleSubjectTax.Exclude') },
                    ]}
                    groupCss={css({ gap: 24 })}
                    style={{ radioSize: 18, labelMargin: 5 }}
                  />
                )}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <div css={inputTextStyle}>
                {t('saleSubjectTax.PlanBreakSettings')}
                <div css={requireLabelTextStyle}>※</div>
              </div>
              <Controller
                control={control}
                name="isIncludeInReservationPrice"
                render={({ field: { onChange, value } }) => (
                  <RadioGroupField
                    onChange={e => onChange(e.currentTarget.value === '1')}
                    value={value ? '1' : '0'}
                    items={[
                      { value: '1', label: t('saleSubjectTax.IncludedPlan') },
                      { value: '0', label: t('saleSubjectTax.NotIncludedPlan') },
                    ]}
                    groupCss={css({ gap: 24 })}
                    style={{ radioSize: 18, labelMargin: 5 }}
                  />
                )}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div css={inputTextStyle}>
                {t('Amount')}
                <div css={requireLabelTextStyle}>※</div>
              </div>
              <Controller
                control={control}
                rules={{ required: t('Required field has not been entered') }}
                name="priceType"
                render={({ field: { onChange, value } }) => (
                  <RadioGroupField
                    onChange={onChange}
                    value={value}
                    items={[
                      { value: SalesSubjectTaxPriceType.FixAmount, label: t('saleSubjectTax.FixAmount') },
                      { value: SalesSubjectTaxPriceType.FixRate, label: t('saleSubjectTax.FixRate') },
                    ]}
                    groupCss={css({ gap: 24 })}
                    style={{ radioSize: 18, labelMargin: 5 }}
                  />
                )}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              {watchPriceType === SalesSubjectTaxPriceType.FixAmount ? (
                <SubjectTaxPriceRangeField control={control} errors={errors} />
              ) : (
                <SubjectTaxRateField control={control} errors={errors} setValue={setValue} resetField={resetField} />
              )}
            </div>

            <div>
              <div css={inputTextStyle}>
                {t('saleSubjectTax.SubjectAuto')}
                <div css={requireLabelTextStyle}>※</div>
              </div>
              <InputSalesSubject control={control} detail={detail} setValue={setValue} disableLabel required />
            </div>

            <div>
              <div css={inputTextStyle}>{t('saleSubjectTax.ApplicablePeriod')}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Controller
                  control={control}
                  name="validFrom"
                  render={({ field: { onChange, value } }) => (
                    <DateField
                      value={value ? moment(value) : undefined}
                      onChange={date => onChange(date ? date.format('YYYY-MM-DD') : '')}
                      displayFormat={t('YYYY-MM-DD')}
                      openDirection="up"
                      placeholder={t('Date')}
                      showClearDate
                      isOutsideRange={day => (watchValidTo ? day.isAfter(moment(watchValidTo), 'day') : false)}
                    />
                  )}
                />
                ~
                <Controller
                  control={control}
                  name="validTo"
                  render={({ field: { onChange, value } }) => (
                    <DateField
                      value={value ? moment(value) : undefined}
                      onChange={date => onChange(date ? date.format('YYYY-MM-DD') : '')}
                      displayFormat={t('YYYY-MM-DD')}
                      openDirection="up"
                      placeholder={t('Date')}
                      isOutsideRange={day => (watchValidFrom ? day.isBefore(moment(watchValidFrom), 'day') : false)}
                      showClearDate
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button buttonType={3} height="38px" width="110px" marginRight={16} onClick={() => onCancel()}>
            {t('Cancel')}
          </Button>
          <Button
            buttonType={isValid ? ButtonType.Primary : ButtonType.PrimaryDisabled}
            height="38px"
            width="110px"
            onClick={handleSubmit(onSave)}
          >
            {t('Save')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

const modalStyle = {
  minHeight: 534,
  maxHeight: 534,
  width: 662,
  minWidth: 662,
  left: 'calc((100% - 662px) / 2)',
  pointerEvents: 'auto',
}

const modalBodyStyle = css({
  padding: 32,
  border: '1px solid #ddd',
  borderRadius: 8,
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
})

const inputTextStyle = css({
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  color: '#676767',
  paddingBottom: 12,
})

const requireLabelTextStyle = css({
  fontSize: 8,
  fontWeight: 'bold',
  letterSpacing: '0.4px',
  color: '#676767',
  paddingLeft: 8,
})

const errorMessageStyle = {
  fontSize: 10,
  color: 'rgba(255,0,0,0.5)',
  marginTop: 4,
}
